import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import {
  EGroupActionType,
  IGroupActionContentBase,
  TGroupActionsLocale,
} from "@rentiohq/shared-frontend/dist/types/groupAction.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { TextField } from "@rentiohq/web-shared/dist/components";
import {
  stateToHTML,
  Editor,
  EditorState,
  ContentState,
} from "@rentiohq/web-shared/dist/components/Editor";
import { isEmpty } from "lodash";
import React from "react";
import { getLocaleKey } from "../GroupActions.util";

export interface IAddGroupActionContentWithEditorSectionProps {
  type: EGroupActionType;
  index: number;
  locale: TGroupActionsLocale;
  initialContent: IGroupActionContentBase;
  children?: React.ReactNode;
  onChange: (content: IGroupActionContentBase) => void;
  onChangeTitle?: (key: TGroupActionsLocale, value?: string) => void;
  onChangeSubject?: (key: TGroupActionsLocale, value?: string) => void;
  onChangeError?: (error?: string) => void;
}

const AddGroupActionContentWithEditorSection = (
  props: IAddGroupActionContentWithEditorSectionProps,
) => {
  const {
    type,
    index,
    locale,
    initialContent,
    onChange,
    onChangeTitle,
    onChangeSubject,
    onChangeError,
  } = props;

  const [editorState, setEditorState] = React.useState<EditorState>(() => {
    const contentState = ContentState.createFromText(
      initialContent.value || "",
    );
    return EditorState.createWithContent(contentState);
  });

  const [contentValue, setContentValue] = React.useState<string>(
    initialContent.value || "",
  );

  const [title, setTitle] = React.useState("");
  const [titleError, setTitleError] = React.useState<string>();
  const [subject, setSubject] = React.useState("");
  const [subjectError, setSubjectError] = React.useState<string>();
  const [contentError, setContentError] = React.useState<string>();

  React.useEffect(() => {
    onChange({ ...initialContent, value: contentValue });
  }, [contentValue]);

  React.useEffect(() => onChangeTitle?.(locale, title), [title]);
  React.useEffect(() => onChangeSubject?.(locale, subject), [subject]);
  React.useEffect(() => {
    onChangeError?.(titleError || subjectError || contentError);
  }, [titleError, subjectError, contentError]);

  const getMaxTextLength = () => {
    if (type === EGroupActionType.Sms) return 160;
    if (type === EGroupActionType.Notification) return 178;
    if ([EGroupActionType.Chat, EGroupActionType.Document].includes(type)) {
      return CONFIG.CHAT_AND_EMAIL_TEXT_LIMIT;
    }
  };

  const handleContentChange = (state: EditorState) => {
    setEditorState(state);
    const contentState = state.getCurrentContent();
    const plain = contentState.getPlainText("").trim();
    setContentValue(plain);
    const html = stateToHTML(contentState);
    onChange({
      ...initialContent,
      value: html,
    });

    if (isEmpty(plain)) {
      setContentError(`The ${initialContent.label} is required`);
    } else {
      setContentError(undefined);
    }
  };

  const renderChildren = () => {
    const lowerCaseType = type.toLowerCase();

    switch (type) {
      case EGroupActionType.Notification:
        return (
          <>
            <TextField
              name={`group_actions_modal_${lowerCaseType}_title_${locale}`}
              label={getLocalizedText(
                `group_actions.modal.title.label.${getLocaleKey(locale)}`,
              )}
              value={title}
              error={titleError}
              type="input"
              onChange={(
                e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>,
              ) => {
                const { value } = e.target;

                if (isEmpty(value)) {
                  setTitleError(
                    `The ${getLocalizedText(
                      `group_actions.modal.title.label.${getLocaleKey(locale)}`,
                    )} is required`,
                  );
                } else {
                  setTitleError(undefined);
                }

                setTitle(value);
              }}
            />
            <Spacer weight={ESpacerWeight.W08} />
          </>
        );

      case EGroupActionType.Email:
        return (
          <>
            <TextField
              name={`group_actions_modal_${lowerCaseType}_subject_${locale}`}
              label={getLocalizedText(
                `group_actions.modal.subject.label.${getLocaleKey(locale)}`,
              )}
              value={subject}
              error={subjectError}
              type="input"
              onChange={(
                e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>,
              ) => {
                const { value } = e.target;

                if (isEmpty(value)) {
                  setSubjectError(
                    `The ${getLocalizedText(
                      `group_actions.modal.subject.label.${getLocaleKey(
                        locale,
                      )}`,
                    )} is required`,
                  );
                } else {
                  setSubjectError(undefined);
                }

                setSubject(value);
              }}
            />
            <Spacer weight={ESpacerWeight.W08} />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {index > 0 && <Spacer weight={ESpacerWeight.W32} />}
      {renderChildren()}
      <Editor
        editorState={editorState}
        onChange={handleContentChange}
        spellCheck
        label={initialContent?.label}
        maxLength={getMaxTextLength()}
        error={contentError}
        helpText={
          [EGroupActionType.Chat, EGroupActionType.Document].includes(type)
            ? getLocalizedText("system.form.error.text.max_length", {
                value: `${CONFIG.CHAT_AND_EMAIL_TEXT_LIMIT}`,
              })
            : undefined
        }
      />
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default AddGroupActionContentWithEditorSection;
